// PubSubController.js
'use strict';
import throttle from 'lodash/throttle';
import { ANALYTICS_GOOGLECLOUD_ACCESS_TOKEN } from '../RemoteConfigKeys.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { pubsub } from '../resource/debug.js';

export const PUBSUB_PREFIX = 'https://pubsub.googleapis.com/v1/';
export const PUBSUB_POSTFIX = ':publish';

export const PUBSUB_LOCAL_STORAGE_KEY = '__pubsub';
const DEFAULT_THROTTLE_INTERVAL = 10 * 1000; // 10 secs

const log = pubsub.extend('log');
const errorLog = pubsub.extend('error');

/**
 * Pub/Sub Controller
 */
class PubSubController {
  /**
   * @param {object} options
   * @param {string} options.pubSubTopic
   * @param {number} [options.batchMaxCount = 10]
   * @param {number} [options.interval = 10000]
   */
  constructor({
    pubSubTopic,
    batchMaxCount = 10,
    interval = DEFAULT_THROTTLE_INTERVAL,
  }) {
    log('create new instance');
    this.queue = {
      pending: [],
      failed: [],
    };
    this.pubSubTopic = pubSubTopic;
    this.batchMaxCount = batchMaxCount;
    this.interval = interval;
    this.flushPendingThrottle = throttle(this.flushPending, this.interval);
    this.flushFailedThrottle = throttle(this.flushFailed, this.interval);
    this.init();
  }

  async init() {
    const storedQueue = this.restore();
    if (storedQueue) {
      const queue = JSON.parse(storedQueue);
      if (queue.pending.length || queue.failed.length) {
        log('flush the stored messages');
        this.queue = queue;
        this.flushPending();
        this.flushFailed();
      }
    }
  }

  async addToQueue(payload) {
    if (payload) {
      try {
        const message = await this.generateBase64Message(payload);
        this._addToQueue(message);
        this.startFlush();
      } catch (error) {
        const { getSentry } = await import('./sentry.js');
        getSentry().then(({ withScope, captureMessage }) => {
          withScope(scope => {
            scope.setFingerprint(['pubsub', 'PubSubController.js']);
            scope.setTag('record', 'pubsub');
            captureMessage(error);
          });
        });
        errorLog(error);
      }
    }
  }

  _addToQueue(messages) {
    this.queue.pending.push(messages);
    this.save();
  }

  setPubSubTopic(topic) {
    this.pubSubTopic = topic;
    this.startFlush();
  }

  save() {
    try {
      localStorage.setItem(
        PUBSUB_LOCAL_STORAGE_KEY,
        JSON.stringify(this.queue)
      );
      // eslint-disable-next-line no-empty
    } catch (_) {}
  }

  restore() {
    let stored;
    try {
      stored = localStorage.getItem(PUBSUB_LOCAL_STORAGE_KEY);
      // eslint-disable-next-line no-empty
    } catch (_) {}

    return stored;
  }

  async startFlush() {
    if (this.queue.pending.length >= this.batchMaxCount) {
      log('flushPending immediately', this.queue.pending.length);
      this.flushPendingThrottle.cancel();
      this.flushPending();
    } else {
      log('flushPendingThrottle', this.queue.pending.length);
      this.flushPendingThrottle();
    }

    log('flushFailedThrottle', this.queue.failed.length);
    this.flushFailedThrottle();
  }

  async flushPending() {
    if (!this.queue.pending.length || !this.pubSubTopic) return;
    const messages = this.queue.pending;
    this.queue.pending = [];
    try {
      await this._flush(messages);
    } catch (_) {
      this.queue.failed.push(...messages);
    } finally {
      this.save();
    }
  }

  async flushFailed() {
    if (!this.queue.failed.length || !this.pubSubTopic) return;
    const messages = this.queue.failed;
    this.queue.failed = [];
    try {
      await this._flush(messages);
    } catch (_) {
      this.queue.failed.push(...messages);
    } finally {
      this.save();
    }
  }

  async _flush(messages) {
    log('flush messages to pub/sub');
    try {
      const pubSubAccessToken = await this.getPubSubAccessToken();
      const pubSubUrl = `${PUBSUB_PREFIX}${this.pubSubTopic}${PUBSUB_POSTFIX}`;

      const response = await fetch(pubSubUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pubSubAccessToken}`,
        },
        body: JSON.stringify({ messages }),
      });

      if (!response.ok) {
        let text = '';
        try {
          text = await response.text();
          // eslint-disable-next-line no-empty
        } catch (_) {}
        throw new Error(`post to pubsub error, ${response.status} ${text}`);
      }
    } catch (error) {
      const { getSentry } = await import('./sentry.js');
      getSentry().then(({ withScope, captureMessage }) => {
        withScope(scope => {
          scope.setFingerprint(['pubsub', 'PubSubController.js']);
          scope.setTag('record', 'pubsub');
          captureMessage(error);
        });
      });
      errorLog(error);
      throw error;
    }
  }

  async generateBase64Message(payload) {
    const { encode } = await import('js-base64');

    return { data: encode(JSON.stringify(payload)) };
  }

  async getPubSubAccessToken() {
    const response = await fetch(
      getResourceUrl({ endpoint: '/configurations/analytics.json' })
    );

    if (!response.ok) {
      let text = '';
      try {
        text = await response.text();
        // eslint-disable-next-line no-empty
      } catch (_) {}
      throw new Error(`getPubSubAccessToken error, ${response.status} ${text}`);
    }
    const data = await response.json();
    return data[ANALYTICS_GOOGLECLOUD_ACCESS_TOKEN];
  }
}

export default PubSubController;
